import {
  useStartLeadershipDayAndConferenceModal,
  useStartLeadershipDayAndConferences
} from '../../../../../hooks/use-startLeadershipDayAndConferences'
import { useState } from 'react'
import { type StartLeadershipDayAndConference } from '../../../../../models/StartLeadershipDayAndConference'
import { BlueButton } from '../../../../customComponents/buttons/BlueButton'
import { AddCircle, CalendarMonth } from '@mui/icons-material'
import { Box, CircularProgress, Typography } from '@mui/material'
import {
  StartLeadershipDayAndConferenceModal,
  type UpdateStartLeadershipDayAndConferenceFormValues
} from './StartLeadershipDayAndConferenceModal'
import { BannerModal } from '../../../../customComponents/BannerModal'
import { DeleteStartLeadershipDayAndConference, DeleteStartLeadershipDayAndConferenceTracking, TrackStartLeadershipDayAndConference } from '../../../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { type ThingsToConsiderSection } from '../ThingsToConsiderCard'
import { BasicTable, type BasicTableColumn } from '../BasicTable'
import { ThingsToConsiderButton } from '../ThingsToConsiderButton'
import { TranslateDate } from '../../../../../utils/dateUtils'
import { useAuth } from '../../../../../hooks/use-auth'
import { AttendancePlanTrackingModal } from './AttendancePlanTrackingModal'
import { SubBasicTable } from '../SubBasicTable'

interface Props {
  priorityId: number
  readOnly: boolean
  trackingReadonly: boolean
  showTableOnly?: boolean
  hideExpand?: boolean
  implementationPhase: boolean
  budgetOverviewView?: boolean
}

export const StartLeadershipDayAndConferenceList = (props: Props): JSX.Element => {
  const [showTrackingModal, setShowTrackingModal] = useState(false)
  const [planBeingEdited, setPlanBeingEdited] = useState<StartLeadershipDayAndConference | null>(null)

  const {
    isLoading,
    leadershipDayAndConferences,
    reloadLeadershipDayAndConferences
  } = useStartLeadershipDayAndConferences(props.priorityId, props.implementationPhase)
  const {
    initialValues,
    handleCloseModal,
    showLeadershipDayAndConferenceModal,
    handleAddLeadershipDayAndConference,
    handleEditLeadershipDayAndConference
  } = useStartLeadershipDayAndConferenceModal()
  const { user } = useAuth()

  const [leadershipDayAndConferenceToDelete, setLeadershipDayAndConferenceToDelete] = useState<StartLeadershipDayAndConference | null>(null)

  const handleDeleteLeadershipDayAndConference = async (): Promise<void> => {
    if (leadershipDayAndConferenceToDelete == null) {
      return
    }

    const { success, errors } = await DeleteStartLeadershipDayAndConference(leadershipDayAndConferenceToDelete.id, leadershipDayAndConferenceToDelete.isUpdate)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    await reloadLeadershipDayAndConferences()
    toast.success('Attendance Plan Deleted')
    setLeadershipDayAndConferenceToDelete(null)
  }

  const handleTrackLeadershipDayAndConference = async (startLeadershipDayAndConference: StartLeadershipDayAndConference): Promise<void> => {
    const dollarAmount: string | undefined = startLeadershipDayAndConference.actualDollarAmount == null
      ? undefined
      : startLeadershipDayAndConference.actualDollarAmount?.toString().replace('$', '')
    setPlanBeingEdited({ ...startLeadershipDayAndConference, actualDollarAmount: dollarAmount })
    setShowTrackingModal(true)
  }

  const deleteTracking = async (startLeadershipDayAndConference: StartLeadershipDayAndConference): Promise<void> => {
    await DeleteStartLeadershipDayAndConferenceTracking(startLeadershipDayAndConference.id)
    await reloadLeadershipDayAndConferences()
    toast.success('Attendance Plan Tracking Deleted')
  }

  const saveTrackingPlanBeingEdited = async (attendees: number, actualBudget: number | null): Promise<void> => {
    if (planBeingEdited != null) {
      const budgetRequestAmount = Number(planBeingEdited.budgetRequest.toString().replace('$', ''))

      const startLeadershipDayAndConference = {
        ...planBeingEdited,
        expandedContent: undefined,
        budgetRequest: budgetRequestAmount,
        actualAttendeeCount: attendees,
        actualDollarAmount: actualBudget?.toString() ?? ''
      }

      setShowTrackingModal(false)
      const { success, errors } = await TrackStartLeadershipDayAndConference(startLeadershipDayAndConference)

      if (!success) {
        errors.forEach(e => toast.error(e))
        return
      }
      await reloadLeadershipDayAndConferences()
      toast.success('Attendance Plan Tracked')
    }
  }

  const editClicked = async (): Promise<void> => {
    const leadershipDayAndConferenceRecord = leadershipDayAndConferences.reduce<Record<string, UpdateStartLeadershipDayAndConferenceFormValues>>((acc, curr) => {
      acc[curr.eventType] = {
        id: curr.id,
        isUpdate: curr.isUpdate,
        priorityId: curr.priorityId,
        eventType: curr.eventType,
        numberOfAttendees: curr.numberOfAttendees,
        budgetRequest: curr.budgetRequest
      }
      return acc
    }, {})

    await handleEditLeadershipDayAndConference(leadershipDayAndConferenceRecord)
  }

  const thingsToConsiderSections: ThingsToConsiderSection[] = [
    {
      title: 'Leadership Day',
      items: ['Invite RCN members, school staff, and administrators who will actively participate and take back the information to facilitate implementation in the region.']
    },
    {
      title: 'Conference',
      items: [
        'RCN leaders, EPLI Trainers, and Coach Leaders are encouraged to attend in order to integrate the new knowledge into their training and coaching practices.',
        'Consider the conference theme and topics to invite school staff and families who will benefit from the learning and share their new knowledge with others.',
        'Invite family members and caregivers (each RCN is designated two complimentary registrations for families).'
      ]
    }
  ]

  const isEmpty = leadershipDayAndConferences.length === 0
  const columns: BasicTableColumn[] = [
    { id: 'trainingTitle', label: 'Training Title', minWidth: 200 },
    { id: 'numberOfAttendees', label: 'Attendance Count', minWidth: 200 },
    { id: 'budgetRequest', label: 'Budget', minWidth: 100 }
  ]
  const rows = leadershipDayAndConferences.map(l => ({
    ...l,
    isUpdated: l.isUpdate,
    isTracked: l.isUpdate && l.actualAttendeeCount != null,
    budgetRequest: `$${l.budgetRequest}`,
    trainingTitle: l.eventType,
    expandedContent: (l.actualAttendeeCount != null &&
      (
        <Box
          sx={{
            p: '2rem',
            backgroundColor: 'var(--grey-100)'
          }}
        >
          <SubBasicTable
            hasTrackingDetails
            readonly={props.readOnly}
            trackingReadonly={props.trackingReadonly}
            onEditTracking={handleTrackLeadershipDayAndConference}
            onDeleteTracking={deleteTracking}
            columns={[
              { id: 'actualAttendeeCount', label: 'ACTUAL ATTENDANCE COUNT', minWidth: 200, isTrackingColumn: true },
              { id: 'actualDollarAmount', label: 'ACTUAL DOLLAR AMOUNT', minWidth: 200, isTrackingColumn: true },
              { id: 'trackingAction', label: '', isTrackingColumn: true }
            ]}
            rows={[{
              ...l,
              id: l.id,
              actualAttendeeCount: l.actualAttendeeCount ?? 0,
              actualDollarAmount: `$${l.actualDollarAmount ?? 0}`
            }]}
            dataTestId={`${l.id}-expandedLeadershipDayAndConferenceTable`}
          />
        </Box>
      )
    )
  }))

  const lock = leadershipDayAndConferences.length > 0 ? leadershipDayAndConferences[0].activityLock : null
  const localLockTime = TranslateDate(lock?.lockedUntil ?? new Date())
  const isLocked = lock != null && lock.lockedBy.id !== user?.id && localLockTime > new Date()

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid='startLeadershipDayAndConferenceList'>
    <Typography variant='h4'>START Leadership Days and Conference</Typography>

    {props.showTableOnly !== true && (
      <Box sx={{ width: '100%', alignItems: 'center' }}>
        {!props.readOnly &&
          <>
            {isEmpty &&
              <BlueButton onClick={handleAddLeadershipDayAndConference} dataTestId='addLeadershipDayAndConferenceButton'>
                Add Attendance Plan <AddCircle sx={{ ml: '10px' }} fontSize='small' />
              </BlueButton>
            }

            {!isEmpty && !isLocked &&
              <BlueButton onClick={editClicked} dataTestId='editLeadershipDayAndConferenceButton'>
                Modify Attendance Plan
              </BlueButton>
            }
          </>
        }

        <Box sx={{ float: 'right' }}>
          <ThingsToConsiderButton
            buttonDataTestId='leadershipDayAndConferenceThingsToConsiderButton'
            thingsToConsiderSections={thingsToConsiderSections}
            cardDataTestId='leadershipDayAndConferenceThingsToConsiderCard'
          />
        </Box>
      </Box>
    )}

    <Box sx={{
      border: '1px solid var(--blue-300)',
      backgroundColor: 'var(--white)',
      borderRadius: '8px',
      padding: isEmpty ? '24px' : '0',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0px 10px 10px -8px #3B353E4D'
    }}
    >
      {isLoading
        ? <CircularProgress sx={{ alignSelf: 'center' }} data-testid='loadingIndicator' />
        : (isEmpty
            ? <>
              <CalendarMonth sx={{ color: 'var(--blue-300)', width: '144px', height: '144px', alignSelf: 'center' }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
                <Typography variant='h4'>No Attendance Plan Added</Typography>
                {!props.readOnly &&
                  <Typography variant='body1' fontSize='16px'>
                    Use the Add Attendance Plan button to add a START Leadership Days and Conference Attendance Plan to your Annual Plan.
                  </Typography>
                }
              </Box>
            </>
            : <BasicTable
                dataTestId='startLeadershipDayAndConferenceTable'
                columns={columns}
                indexRowIds={props.implementationPhase}
                rows={rows}
                isLoading={isLoading}
                readonly={props.readOnly}
                trackingReadonly={props.trackingReadonly}
                onEdit={editClicked}
                onDelete={setLeadershipDayAndConferenceToDelete}
                onUpdate={editClicked}
                onTrack={handleTrackLeadershipDayAndConference}
                hideExpand={props.hideExpand}
                implementationPhase={props.implementationPhase}
                budgetOverviewView={props.budgetOverviewView}
            />)
      }

      {showLeadershipDayAndConferenceModal &&
        <StartLeadershipDayAndConferenceModal
          open={showLeadershipDayAndConferenceModal}
          priorityId={props.priorityId}
          onClose={handleCloseModal}
          onSaved={reloadLeadershipDayAndConferences}
          initialValues={initialValues}
          isImplementationPhase={props.implementationPhase}
        />
      }

      {leadershipDayAndConferenceToDelete != null &&
        <BannerModal
          title='Are you sure?'
          cancelButtonText='No, Cancel'
          confirmButtonText='Yes, Delete'
          dataTestId='confirmDeleteLeadershipDayAndConferenceModal'
          bodyContent={`Are you sure you want to delete this item?${leadershipDayAndConferenceToDelete.actualAttendeeCount != null ? ' This will also remove any tracking data associated with it.' : ''}`}
          onClose={() => { setLeadershipDayAndConferenceToDelete(null) }}
          onConfirm={handleDeleteLeadershipDayAndConference}
          maxWidth='xs'
          bottom=''
          position='fixed'
        />
      }

      {showTrackingModal &&
        <AttendancePlanTrackingModal
          title={`${planBeingEdited?.eventType ?? 'Event Attendance'} Tracking`}
          onSave={saveTrackingPlanBeingEdited}
          attendees={planBeingEdited?.actualAttendeeCount?.toString() ?? ''}
          budget={planBeingEdited?.actualDollarAmount ?? ''}
          onCancel={() => { setShowTrackingModal(false) }}
        />
      }
    </Box>
  </Box>
}
